import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";

/**
 * ForgotPassword Component
 *
 * Allows users to request a password reset email.
 *
 * Expected API Endpoint (to be implemented):
 * POST /api/v1/auth/forgot-password
 * Request: { email: string }
 * Response: { message: string }
 */
export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await apiService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send reset email",
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "50px auto",
          padding: "30px",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            marginBottom: "20px",
            textAlign: "center",
            color: "#28a745",
          }}
        >
          Email Sent
        </h2>
        <div
          style={{
            padding: "16px",
            background: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          <p style={{ margin: 0 }}>
            If an account exists with the email <strong>{email}</strong>, you
            will receive password reset instructions shortly.
          </p>
        </div>
        <p style={{ textAlign: "center", color: "#6c757d", fontSize: "14px" }}>
          Please check your email and follow the instructions to reset your
          password.
        </p>
        <button
          onClick={() => navigate("/login")}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
            marginTop: "20px",
          }}
        >
          Back to Login
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "400px",
        margin: "50px auto",
        padding: "30px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ marginBottom: "10px", textAlign: "center" }}>
        Forgot Password
      </h2>
      <p
        style={{
          textAlign: "center",
          color: "#6c757d",
          marginBottom: "30px",
          fontSize: "14px",
        }}
      >
        Enter your email address and we'll send you instructions to reset your
        password.
      </p>

      {error && (
        <div
          style={{
            padding: "12px",
            background: "#f8d7da",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginBottom: "20px",
            color: "#721c24",
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "500",
              fontSize: "14px",
            }}
          >
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="your.email@example.com"
            disabled={loading}
            style={{
              width: "100%",
              padding: "10px",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !email}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: loading || !email ? "#6c757d" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: loading || !email ? "not-allowed" : "pointer",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          {loading ? "Sending..." : "Send Reset Instructions"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/login")}
          disabled={loading}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "transparent",
            color: "#007bff",
            border: "none",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "14px",
            marginTop: "10px",
          }}
        >
          Back to Login
        </button>
      </form>
    </div>
  );
};
